import Loader from 'components/base/loader';
import SecurityLayout from 'layouts/security.layout';
import { OrderType } from 'models/order.model';
import React, { Fragment, useEffect, useState } from 'react';
import { apiService } from 'utils/api';
import { SECURITY_PICKUP } from 'utils/constants';
import { ENUM_ITEM_TYPE } from 'utils/enum';
import { formatTime, getNameFromOrder } from 'utils/functions';
import { SocketIOConnection } from 'utils/socket_client';

const baseUrl = process.env.REACT_APP_KITCHEN_API_URL;

const checkOrderValidity = (order: OrderType): boolean => {
  const kitchen_ready = order.calculated_order.item_type === ENUM_ITEM_TYPE.FOOD;
  const shop_ready = order.calculated_order.item_type === ENUM_ITEM_TYPE.SHOP;
  const all_ready = order.calculated_order.item_type === ENUM_ITEM_TYPE.ALL;
  return (kitchen_ready || shop_ready || all_ready) && order.calculated_order.pickup;
};

const getFrontDeskOrders = async () => {
  const res = await apiService.get('orders/frondesk/dispatch');
  return res.data.data;
};

const getKitchenOrders = async () => {
  const res = await apiService.get('orders/kitchen/active', { baseUrl });
  return res.data.data;
};

const Security = () => {
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState<OrderType[]>([]);
  const [removedOrder, setRemovedOrder] = useState<OrderType>();
  const [updatedOrder, setUpdatedOrder] = useState<OrderType>();

  const loadOrders = async () => {
    setLoading(true);
    const [kitchen, frontDesk] = await Promise.all([getKitchenOrders(), getFrontDeskOrders()]);
    const pickupOrders = [...kitchen, ...frontDesk]
      .filter((o) => o.calculated_order.pickup)
      .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    setOrders(pickupOrders);
    setLoading(false);
  };

  const initPage = async (connection: any) => {
    await loadOrders();
    await connection.joinRoom('order-update', process.env.REACT_APP_WEBOCKETAPP_KEY);
  };

  const removeOrderIfExist = (order?: OrderType) => {
    if (!order) return;
    const updatedAllOrders = orders
      .filter((item) => item.id !== order.id)
      .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    setOrders(updatedAllOrders);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const connection = await SocketIOConnection.getConnection();
      connection.onReconnect(() => initPage(connection));
      connection.addEventListener('order-update', 'updated', (order: OrderType) => {
        setUpdatedOrder(order);
      });
      connection.addEventListener('order-update', 'removed', (order: OrderType) => {
        setRemovedOrder(order);
      });
      initPage(connection);
      return () => {
        connection.removeListener('order-update', 'updated');
        connection.removeListener('order-update', 'removed');
      };
    })();
  }, []);

  const handleUpdatedOrder = () => {
    if (!updatedOrder) return;
    const isValidOrder = checkOrderValidity(updatedOrder);
    if (isValidOrder) {
      const updatedAllOrders = orders.filter((item) => updatedOrder.id !== item.id);
      const oldOrder = orders.find((o) => o.id === updatedOrder.id);
      if (oldOrder) {
        updatedAllOrders.push({ ...oldOrder, ...updatedOrder });
        updatedAllOrders.sort(
          (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
        setOrders(updatedAllOrders);
      }
    } else {
      removeOrderIfExist(updatedOrder);
    }
  };

  const getPickupStatus = (order: OrderType) => {
    const key =
      (order.calculated_order.item_type === ENUM_ITEM_TYPE.FOOD && order.kitchen_prepared) ||
      (order.calculated_order.item_type === ENUM_ITEM_TYPE.SHOP && order.shop_prepared) ||
      (order.calculated_order.item_type === ENUM_ITEM_TYPE.ALL &&
        order.shop_prepared &&
        order.kitchen_prepared)
        ? 'ready'
        : 'pending';
    return SECURITY_PICKUP[key];
  };

  useEffect(() => {
    removeOrderIfExist(removedOrder);
  }, [removedOrder]);

  useEffect(handleUpdatedOrder, [updatedOrder]);

  return (
    <SecurityLayout>
      {loading ? (
        <Loader className='min-h-[80vh]' />
      ) : orders.length > 0 ? (
        <Fragment>
          <div className='grid grid-cols-6 border-b border-gray-border py-1 font-medium'>
            <div className='col-span-2'>Name</div>
            <div className='col-span-2'>Order No.</div>
            <div>Created</div>
            <div />
          </div>
          {orders.map((order) => (
            <div key={order.id} className='grid grid-cols-6 gap-1 border-t border-gray-border py-3'>
              <div className='col-span-2 break-words'>{getNameFromOrder(order)}</div>
              <div className='col-span-2 uppercase font-medium'>#{order.order_code}</div>
              <div>{formatTime(order.created_at)}</div>
              <div className='text-center'>
                <span
                  style={{ ...getPickupStatus(order) }}
                  className={`rounded-full inline-block w-3 h-3`}
                />
              </div>
            </div>
          ))}
        </Fragment>
      ) : (
        <div className='h-[50vh] flex items-center justify-center'>
          There are no pickup orders currently
        </div>
      )}
    </SecurityLayout>
  );
};

export default Security;
